import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import {
    ArrowLeftIcon,
    CalendarDaysIcon,
    CheckBadgeIcon,
    UserGroupIcon,
    BarsArrowDownIcon,
    ShieldCheckIcon,
    XCircleIcon,
    MagnifyingGlassIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ClassAttendanceManager = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { get, post } = useApi();
    
    const { gradeId, streamId } = location.state || {};
    const { showToast } = useAppState();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [attendanceData, setAttendanceData] = useState([]);
    const [initialAttendance, setInitialAttendance] = useState([]);
    const [isCommitted, setIsCommitted] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchAttendance = useCallback(async () => {
        try {
            setLoading(true);
            let url = `/api/teacher/class-teacher/attendance_for_date/?date=${selectedDate}`;
            if (gradeId) url += `&grade_level=${gradeId}`;
            if (streamId) url += `&stream_id=${streamId}`;
            const data = await get(url);
            const attendance = data.attendance || [];
            setAttendanceData(attendance);
            setInitialAttendance(JSON.parse(JSON.stringify(attendance)));
            setIsCommitted(false);
        } catch (error) {
            console.error('Error fetching attendance:', error);
            showToast('Failed to load attendance records', 'error');
        } finally {
            setLoading(false);
        }
    }, [selectedDate, get, showToast, gradeId, streamId]);

    useEffect(() => {
        fetchAttendance();
    }, [fetchAttendance]);

    const handleStatusUpdate = (studentId, newStatus) => {
        setAttendanceData(prev => prev.map(item =>
            item.student_id === studentId ? { ...item, status: newStatus } : item
        ));
        if (isCommitted) setIsCommitted(false);
    };

    const markAllPresent = () => {
        setAttendanceData(prev => prev.map(item => ({ ...item, status: 'Present' })));
        setIsCommitted(false);
        showToast('All students marked as present');
    };

    const saveAttendance = async () => {
        try {
            setSaving(true);
            const payload = {
                date: selectedDate,
                attendance: attendanceData.map(item => ({
                    student_id: item.student_id,
                    status: item.status || 'Absent'
                }))
            };
            let url = `/api/teacher/class-teacher/mark_attendance/`;
            if (gradeId || streamId) {
                url += `?`;
                if (gradeId) url += `grade_level=${gradeId}`;
                if (streamId) url += `${gradeId ? '&' : ''}stream_id=${streamId}`;
            }
            await post(url, payload);

            // Sync Query Cache for Dashboard Stats
            queryClient.invalidateQueries({ queryKey: ['class-teacher-dashboard'] });

            setIsCommitted(true);
            setInitialAttendance(JSON.parse(JSON.stringify(attendanceData)));
            showToast('Attendance registry synchronized successfully', 'success');
            // Don't navigate away, let them see it saved
        } catch (error) {
            console.error('Error saving attendance:', error);
            showToast('Failed to save attendance registry', 'error');
        } finally {
            setSaving(false);
        }
    };

    const filteredAttendance = attendanceData
        .filter(item =>
            item.student_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (item.registration_id && item.registration_id.toLowerCase().includes(searchQuery.toLowerCase()))
        )
        .sort((a, b) => a.student_name.localeCompare(b.student_name));

    const stats = {
        total: attendanceData.length,
        present: attendanceData.filter(i => i.status === 'Present').length,
        absent: attendanceData.filter(i => i.status === 'Absent').length,
        pending: attendanceData.filter(i => !i.status).length,
        rate: attendanceData.length ? Math.round((attendanceData.filter(i => i.status === 'Present').length / attendanceData.length) * 100) : 0
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC]">
            {/* Premium Header */}
            <div className="bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
                <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-center gap-6">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-3 bg-slate-50 rounded-2xl text-slate-400 hover:text-[#18216D] hover:bg-slate-100 transition-all border border-slate-100"
                        >
                            <ArrowLeftIcon className="w-5 h-5" />
                        </button>
                        <div>
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-[10px] font-black text-[#18216D]/40 uppercase tracking-[0.2em]">Class Teacher Hub</span>
                                <div className="h-1 w-1 bg-slate-200 rounded-full" />
                                <span className="text-[10px] font-black text-emerald-500 uppercase tracking-widest italic">Live Registry</span>
                            </div>
                            <h1 className="text-3xl font-black text-[#18216D] tracking-tighter">Attendance Manager</h1>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-4">
                        <div className="relative w-full sm:w-64">
                            <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search scholars..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12 pr-6 py-4 bg-slate-50 border-none rounded-2xl font-black text-[#18216D] text-xs focus:ring-2 focus:ring-indigo-600/10 shadow-inner placeholder:text-slate-300"
                            />
                        </div>
                        <div className="relative">
                            <CalendarDaysIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-300" />
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="pl-12 pr-6 py-4 bg-slate-50 border-none rounded-2xl font-black text-[#18216D] text-xs focus:ring-2 focus:ring-indigo-600/10 cursor-pointer shadow-inner"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-6 py-8">
                {loading ? (
                    <div className="py-32 flex flex-col items-center">
                        <LoadingSpinner />
                        <p className="mt-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] animate-pulse">Accessing Registry Records...</p>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {/* Stats Ribbons */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center justify-between group hover:shadow-xl hover:shadow-indigo-900/5 transition-all">
                                <div>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Present Today</p>
                                    <p className="text-3xl font-black text-emerald-600 tracking-tighter">{stats.present}</p>
                                </div>
                                <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform">
                                    <CheckBadgeIcon className="w-6 h-6" />
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center justify-between group hover:shadow-xl hover:shadow-indigo-900/5 transition-all">
                                <div>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Absent Today</p>
                                    <p className="text-3xl font-black text-rose-600 tracking-tighter">{stats.absent}</p>
                                </div>
                                <div className="w-12 h-12 bg-rose-50 rounded-2xl flex items-center justify-center text-rose-600 group-hover:scale-110 transition-transform">
                                    <XCircleIcon className="w-6 h-6" />
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center justify-between group hover:shadow-xl hover:shadow-indigo-900/5 transition-all">
                                <div>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Completion Rate</p>
                                    <p className="text-3xl font-black text-indigo-600 tracking-tighter">{stats.rate}%</p>
                                </div>
                                <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 group-hover:scale-110 transition-transform">
                                    <ShieldCheckIcon className="w-6 h-6" />
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-start flex-col justify-center">
                                <button
                                    onClick={markAllPresent}
                                    className="w-full py-4 bg-emerald-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-lg shadow-emerald-500/20 hover:bg-emerald-600 transition-all active:scale-95 flex items-center justify-center gap-3"
                                >
                                    <UserGroupIcon className="w-5 h-5" />
                                    Mark All Present
                                </button>
                            </div>
                        </div>

                        <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-900/5 border border-white overflow-hidden">
                            <div className="px-8 py-6 bg-slate-50/50 border-b border-white flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <BarsArrowDownIcon className="w-5 h-5 text-slate-400" />
                                    <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Scholar Roster</h2>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-slate-50 border-b border-white text-left">
                                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Scholar Details</th>
                                            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Registration ID</th>
                                            <th className="px-8 py-5 text-center text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Daily Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {filteredAttendance.map((item) => (
                                            <tr key={item.student_id} className="hover:bg-slate-50/80 transition-all group">
                                                <td className="px-8 py-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-50 to-slate-50 flex items-center justify-center text-[#18216D] font-black text-[9px] shadow-sm border border-indigo-100/50">
                                                            {item.student_name.split(' ').map(n => n[0]).join('')}
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-black text-[#18216D] tracking-tight group-hover:text-indigo-600 transition-colors uppercase">{item.student_name}</h3>
                                                            {item.is_flagged ? (
                                                                <span className="text-[7px] font-black bg-rose-500 text-white px-2 py-0.5 rounded-full uppercase tracking-widest animate-pulse flex items-center gap-1 w-fit mt-1">
                                                                    <ShieldCheckIcon className="w-2 h-2" />
                                                                    Flagged Conflict
                                                                </span>
                                                            ) : (
                                                                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">
                                                                    {(gradeId || streamId) && item.grade_stream ? item.grade_stream : 'Grade Scholar'}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-4">
                                                    <span className="text-[11px] font-black text-[#18216D]/60 bg-white px-3 py-1.5 rounded-xl border border-slate-100 shadow-sm font-mono">
                                                        {item.registration_id || `ID-${item.student_id}`}
                                                    </span>
                                                </td>
                                                <td className="px-8 py-4">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button
                                                            onClick={() => handleStatusUpdate(item.student_id, 'Present')}
                                                            className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all border-2 ${item.status === 'Present' ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20' : 'bg-white border-slate-100 text-slate-300 hover:border-emerald-200 hover:text-emerald-500'}`}
                                                        >
                                                            <CheckCircleIcon className="w-4 h-4" />
                                                            Present
                                                        </button>
                                                        <button
                                                            onClick={() => handleStatusUpdate(item.student_id, 'Absent')}
                                                            className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all border-2 ${item.status === 'Absent' ? 'bg-rose-500 border-rose-500 text-white shadow-lg shadow-rose-500/20' : 'bg-white border-slate-100 text-slate-300 hover:border-rose-200 hover:text-rose-500'}`}
                                                        >
                                                            <XCircleIcon className="w-4 h-4" />
                                                            Absent
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {filteredAttendance.length === 0 && (
                                    <div className="py-24 flex flex-col items-center">
                                        <UserGroupIcon className="w-12 h-12 text-slate-200 mb-4" />
                                        <p className="text-slate-400 font-black text-[10px] uppercase tracking-[0.2em]">
                                            {searchQuery ? `No scholars match "${searchQuery}"` : 'No scholars found in registry'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-between items-center bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl shadow-indigo-900/5">
                            <div>
                                <h4 className="text-[10px] font-black text-[#18216D] uppercase tracking-widest mb-1">Commitment Policy</h4>
                                <p className="text-[9px] text-slate-400 font-bold max-w-sm">By saving this registry, you are certifying the attendance records for this date as official school data.</p>
                            </div>
                            <div className="flex gap-4">
                                <button
                                    onClick={() => navigate('/teacher/class-dashboard')}
                                    className="px-8 py-4 text-slate-400 font-black text-[10px] uppercase tracking-widest hover:text-slate-600 rounded-2xl transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={saveAttendance}
                                    disabled={saving || (!isCommitted && JSON.stringify(attendanceData) === JSON.stringify(initialAttendance) && attendanceData.length > 0)}
                                    className={`flex items-center gap-4 px-12 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl active:scale-95 transition-all disabled:opacity-50 ${isCommitted && JSON.stringify(attendanceData) === JSON.stringify(initialAttendance) ? 'bg-emerald-600 text-white shadow-emerald-900/30' : 'bg-[#18216D] text-white hover:bg-[#0D164F] shadow-indigo-900/30'}`}
                                >
                                    {saving ? (
                                        <>
                                            <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                            Syncing...
                                        </>
                                    ) : isCommitted && JSON.stringify(attendanceData) === JSON.stringify(initialAttendance) ? (
                                        <>
                                            <CheckBadgeIcon className="w-5 h-5" />
                                            Committed
                                        </>
                                    ) : (
                                        <>
                                            <ShieldCheckIcon className="w-5 h-5" />
                                            Commit Change
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default ClassAttendanceManager;
